import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Landmark, Upload, ShieldCheck, Lock, BarChart3, Package, Zap, Link2 } from "lucide-react";
import { PageHeader } from "../components/ui/PageHeader";
import { Button } from "../components/ui/Button";
import { TransactionsTable } from "../components/bank/TransactionsTable";
import { MatchSuggestionPanel } from "../components/bank/MatchSuggestionPanel";
import { ImportWizard } from "../components/bank/ImportWizard";
import { ControlPanel } from "../components/bank/ControlPanel";
import { BankAccountsPanel } from "../components/bank/BankAccountsPanel";
import { MonthClosePanel } from "../components/bank/MonthClosePanel";
import { ReportsPanel } from "../components/bank/ReportsPanel";
import { AccountantPackagePanel } from "../components/bank/AccountantPackagePanel";
import { TableExportToolbar } from "../components/TableExportToolbar";
import { eur, fmtDate } from "../lib/format";
import api from "../lib/api";
import type { BankTransaction } from "../types/bank";
import type { Column } from "../components/ui/DataTable";
import { matchStatusLabels } from "../types/bank";

/** Export-kolommen voor de CSV/PDF-export van de transactielijst. */
const txColumns: Column<BankTransaction>[] = [
  { key: "date", header: "Datum", accessor: (t) => fmtDate(t.date), sortValue: (t) => new Date(t.date).getTime() },
  { key: "counterparty", header: "Tegenpartij", accessor: (t) => t.counterparty || "—" },
  { key: "description", header: "Omschrijving", accessor: (t) => t.description || "—" },
  { key: "amountEur", header: "Bedrag", accessor: (t) => eur(Number(t.amountEur)), className: "text-right", sortValue: (t) => Number(t.amountEur) },
  { key: "matchStatus", header: "Status", accessor: (t) => matchStatusLabels[t.matchStatus] },
  { key: "link", header: "Koppeling", accessor: (t) => t.invoice?.number || t.expense?.description || (t.matchRule === "BOOK_INTERNAL" ? (t.matchNote || "Geboekt zonder koppeling") : "—") },
];

type Tab = "rekeningen" | "transacties" | "importeren" | "controle" | "afsluiting" | "rapporten" | "accountant";

const tabs: { key: Tab; label: string; icon: typeof Landmark }[] = [
  { key: "rekeningen", label: "Bankrekeningen", icon: Link2 },
  { key: "transacties", label: "Transacties", icon: Landmark },
  { key: "importeren", label: "Importeren", icon: Upload },
  { key: "controle", label: "Controle", icon: ShieldCheck },
  { key: "afsluiting", label: "Maandafsluiting", icon: Lock },
  { key: "rapporten", label: "Rapporten", icon: BarChart3 },
  { key: "accountant", label: "Accountant-pakket", icon: Package },
];

/** Banktransacties: bankrekeningen (banksync) → importeren → matchen → controle → afsluiting → rapporten → accountant. */
export default function BankTransactions() {
  // Terugkeer van de bank na koppeling: ?ref=<reference> → Bankrekeningen-tab
  // waar het panel de koppeling afrondt (POST /complete).
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>(() => {
    // Deep-link via ?tab=afsluiting e.d. (bv. vanuit het Dashboard-rapporten).
    const t = params.get("tab");
    if (t && tabs.some((x) => x.key === t)) return t as Tab;
    return params.get("ref") ? "rekeningen" : "transacties";
  });
  const [returnReference, setReturnReference] = useState<string | null>(params.get("ref"));
  const [txs, setTxs] = useState<BankTransaction[]>([]);
  const [selected, setSelected] = useState<BankTransaction[]>([]);
  const [visibleRows, setVisibleRows] = useState<BankTransaction[]>([]);
  const [matchTx, setMatchTx] = useState<BankTransaction | null>(null);
  const [autoMatching, setAutoMatching] = useState(false);

  const loadTxs = useCallback(() => {
    api
      .get("/api/bank-transactions")
      .then((r) => setTxs(r.data ?? []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadTxs();
  }, [loadTxs]);

  const afterChange = () => {
    loadTxs();
  };

  const runAutoMatch = async () => {
    setAutoMatching(true);
    try {
      const { data } = await api.post("/api/bank-transactions/auto-match");
      alert(
        `Auto-match klaar: ${data.autoMatched} automatisch gekoppeld, ${data.suggested} voorstellen gegenereerd.\n\nBekijk de voorstellen in de tabel en bevestig ze via het koppel-icoon.`,
      );
      loadTxs();
    } catch (err: any) {
      alert(err?.response?.data?.error || "Auto-match mislukt.");
    } finally {
      setAutoMatching(false);
    }
  };

  const act = async (tx: BankTransaction | null, action: "unmatch" | "ignore" | "unignore") => {
    if (!tx) return;
    if (action === "ignore" && !confirm("Deze transactie negeren? Hij telt dan niet meer mee bij de maandafsluiting.")) return;
    try {
      await api.post(`/api/bank-transactions/${tx.id}/${action}`);
      loadTxs();
    } catch (err: any) {
      alert(err?.response?.data?.error || "Actie mislukt.");
    }
  };

  const unmatchedCount = txs.filter((t) => t.matchStatus === "UNMATCHED" || t.matchStatus === "SUGGESTED").length;
  const saldo = txs.reduce((s, t) => s + Number(t.amountEur || 0), 0);
  const exportRows = selected.length ? selected : visibleRows;

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader
        title="Banktransacties"
        subtitle="Importeer bankafschriften, koppel transacties en sluit je maanden af"
        action={
          tab === "transacties" ? (
            <div className="flex items-center gap-3">
              {unmatchedCount > 0 && (
                <span className="text-sm font-semibold text-amber-600">{unmatchedCount} open</span>
              )}
              <Button onClick={runAutoMatch} loading={autoMatching}>
                <Zap size={18} /> Auto-match
              </Button>
            </div>
          ) : undefined
        }
      />

      <div className="flex gap-1 bg-slate-100 rounded-xl p-1 overflow-x-auto">
        {tabs.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all ${
              tab === key ? "bg-white text-slate-800 shadow" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <Icon size={16} /> {label}
          </button>
        ))}
      </div>

      {tab === "transacties" && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="Transacties" value={String(txs.length)} />
            <Stat label="Nog te verwerken" value={String(unmatchedCount)} tone={unmatchedCount > 0 ? "amber" : "emerald"} />
            <Stat label="Gekoppeld" value={String(txs.filter((t) => t.matchStatus === "MATCHED").length)} tone="emerald" />
            <Stat label="Mutatiesom" value={eur(saldo)} tone={saldo >= 0 ? "emerald" : "rose"} />
          </div>
          <TransactionsTable
            rows={txs}
            selected={selected}
            onSelectionChange={setSelected}
            onVisibleRowsChange={setVisibleRows}
            onMatch={(t) => setMatchTx(t)}
            onUnmatch={(t) => act(t, "unmatch")}
            onIgnore={(t) => act(t, "ignore")}
            onUnignore={(t) => act(t, "unignore")}
          />
          <div className="flex justify-end">
            <TableExportToolbar
              rows={exportRows}
              columns={txColumns}
              filename={selected.length ? "banktransacties-selectie" : "banktransacties"}
              source="banktransacties"
              printTitle="Banktransacties"
            />
          </div>
        </div>
      )}

      {tab === "rekeningen" && (
        <BankAccountsPanel
          returnReference={returnReference}
          onReturnDone={() => {
            setReturnReference(null);
            params.delete("ref");
            setParams(params, { replace: true });
          }}
          onChanged={afterChange}
        />
      )}
      {tab === "importeren" && <ImportWizard onImported={afterChange} />}
      {tab === "controle" && <ControlPanel onGoToTransactions={() => setTab("transacties")} />}
      {tab === "afsluiting" && <MonthClosePanel onChanged={afterChange} />}
      {tab === "rapporten" && <ReportsPanel />}
      {tab === "accountant" && <AccountantPackagePanel />}

      <MatchSuggestionPanel
        isOpen={!!matchTx}
        onClose={() => setMatchTx(null)}
        tx={matchTx}
        onDone={afterChange}
      />
    </div>
  );
}

function Stat({ label, value, tone = "slate" }: { label: string; value: string; tone?: "slate" | "amber" | "emerald" | "rose" }) {
  const toneCls =
    tone === "amber" ? "text-amber-600" : tone === "emerald" ? "text-emerald-600" : tone === "rose" ? "text-rose-600" : "text-slate-800";
  return (
    <div className="surface-card p-4">
      <p className={`text-2xl font-bold ${toneCls}`}>{value}</p>
      <p className="text-sm text-slate-500 font-medium">{label}</p>
    </div>
  );
}